"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { updateDropInCachedDrops } from "@/components/react-query-wrapper/utils/updateAttachmentInCachedDrops";
import { ProcessIncomingDropType } from "@/contexts/wave/hooks/useWaveRealtimeUpdater";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPoll } from "@/generated/models/ApiDropPoll";
import { voteDropPollV2 } from "@/services/api/wave-drops-v2-api";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type SyntheticEvent, useCallback, useState } from "react";
import { PollOptionVoters } from "./PollOptionVoters";
import { PollResultOption } from "./PollResultOption";
import { PollVoteOption } from "./PollVoteOption";
import { getOptimisticPoll, getPollClosingLabel, getPollSubmitLabel } from "./WaveDropPoll.helpers";
type PollView = "vote" | "results";
interface WaveDropPollProps {
  readonly drop: ApiDrop;
}
interface LocalPollOverride {
  readonly sourcePoll: ApiDropPoll | undefined;
  readonly poll: ApiDropPoll | null;
}
interface PollInteractionState {
  readonly pollKey: string;
  readonly view: PollView;
  readonly selectedOptionNos: readonly number[];
  readonly expandedOptionNo: number | null;
}
const getDefaultPollView = (poll: ApiDropPoll): PollView => poll.is_open && poll.voted.length === 0 ? "vote" : "results";
const getVotedKey = (poll: ApiDropPoll): string => [...poll.voted].sort((a, b) => a - b).join(",");
const getPollInteractionKey = (poll: ApiDropPoll): string => `${poll.id}:${poll.is_open ? "open" : "closed"}:${getVotedKey(poll)}`;
const getDefaultPollInteractionState = (poll: ApiDropPoll): PollInteractionState => ({
  pollKey: getPollInteractionKey(poll),
  view: getDefaultPollView(poll),
  selectedOptionNos: [...poll.voted],
  expandedOptionNo: null
});
const getScopedPollInteractionState = (current: PollInteractionState | null, poll: ApiDropPoll): PollInteractionState => {
  const pollKey = getPollInteractionKey(poll);
  return current?.pollKey === pollKey ? current : getDefaultPollInteractionState(poll);
};
const stopPollEventPropagation = (event: SyntheticEvent) => {
  event.stopPropagation();
};
export default function WaveDropPoll(t0) {
  const $ = _c(74);
  const {
    drop
  } = t0;
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const myStream = useMyStreamOptional();
  const [localPollOverride, setLocalPollOverride] = useState(null);
  const localPoll = localPollOverride && localPollOverride.sourcePoll === drop.poll ? localPollOverride.poll : null;
  const poll = localPoll ?? drop.poll;
  const [interactionState, setInteractionState] = useState(null);
  let t1;
  if ($[0] !== drop) {
    t1 = async options => await voteDropPollV2({
      drop,
      options
    });
    $[0] = drop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== drop.poll || $[3] !== myStream || $[4] !== queryClient) {
    t2 = updatedDrop => {
      if (updatedDrop.poll) {
        setLocalPollOverride({
          sourcePoll: drop.poll,
          poll: updatedDrop.poll
        });
        setInteractionState({
          ...getDefaultPollInteractionState(updatedDrop.poll),
          view: "results"
        });
      }
      updateDropInCachedDrops(queryClient, updatedDrop);
      myStream?.processIncomingDrop(updatedDrop, ProcessIncomingDropType.DROP_INSERT);
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROP_POLL_VOTERS]
      }).catch(_temp);
    };
    $[2] = drop.poll;
    $[3] = myStream;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== setToast) {
    t3 = error => {
      setToast({
        message: error instanceof Error ? error.message : String(error),
        type: "error"
      });
    };
    $[6] = setToast;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const voteMutation = useMutation(t4);
  let t5;
  if ($[12] !== drop.poll || $[13] !== interactionState || $[14] !== localPollOverride || $[15] !== poll || $[16] !== requestAuth || $[17] !== voteMutation) {
    t5 = async selectedOptionNos => {
      if (!poll?.is_open || selectedOptionNos.length === 0) {
        return false;
      }
      const {
        success
      } = await requestAuth();
      if (!success) {
        return false;
      }
      const previousLocalPollOverride = localPollOverride;
      const previousInteractionState = interactionState;
      const optimisticPoll = getOptimisticPoll(poll, selectedOptionNos);
      setLocalPollOverride({
        sourcePoll: drop.poll,
        poll: optimisticPoll
      });
      setInteractionState({
        ...getDefaultPollInteractionState(optimisticPoll),
        view: "results"
      });
      voteMutation.mutate(selectedOptionNos, {
        onError: () => {
          setLocalPollOverride(previousLocalPollOverride);
          setInteractionState(previousInteractionState);
        }
      });
      return true;
    };
    $[12] = drop.poll;
    $[13] = interactionState;
    $[14] = localPollOverride;
    $[15] = poll;
    $[16] = requestAuth;
    $[17] = voteMutation;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const submitPollVote = t5;
  let t6;
  if ($[19] !== poll || $[20] !== submitPollVote) {
    t6 = optionNo => {
      if (!poll) {
        return;
      }
      if (!poll.multichoice) {
        setInteractionState(current => {
          const scopedState = getScopedPollInteractionState(current, poll);
          return {
            ...scopedState,
            selectedOptionNos: [optionNo]
          };
        });
        submitPollVote([optionNo]).then(submitted => {
          if (submitted) {
            return;
          }
          setInteractionState(current_0 => {
            const scopedState_0 = getScopedPollInteractionState(current_0, poll);
            if (scopedState_0.selectedOptionNos.length !== 1 || scopedState_0.selectedOptionNos[0] !== optionNo) {
              return scopedState_0;
            }
            return {
              ...scopedState_0,
              selectedOptionNos: [...poll.voted]
            };
          });
        }).catch(_temp2);
        return;
      }
      setInteractionState(current_1 => {
        const scopedState_1 = getScopedPollInteractionState(current_1, poll);
        const next = new Set(scopedState_1.selectedOptionNos);
        if (next.has(optionNo)) {
          next.delete(optionNo);
        } else {
          next.add(optionNo);
        }
        return {
          ...scopedState_1,
          selectedOptionNos: [...next].sort(_temp3)
        };
      });
    };
    $[19] = poll;
    $[20] = submitPollVote;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleVoteOptionChange = t6;
  let t7;
  if ($[22] !== interactionState || $[23] !== poll || $[24] !== submitPollVote) {
    t7 = async () => {
      if (!poll?.is_open) {
        return;
      }
      const scopedState_2 = getScopedPollInteractionState(interactionState, poll);
      if (scopedState_2.selectedOptionNos.length === 0) {
        return;
      }
      await submitPollVote(scopedState_2.selectedOptionNos);
    };
    $[22] = interactionState;
    $[23] = poll;
    $[24] = submitPollVote;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleSubmitVote = t7;
  let t8;
  if ($[26] !== poll) {
    t8 = () => {
      if (!poll?.is_open) {
        return;
      }
      setInteractionState(current_2 => {
        const scopedState_3 = getScopedPollInteractionState(current_2, poll);
        return {
          ...scopedState_3,
          view: "vote",
          selectedOptionNos: [...poll.voted],
          expandedOptionNo: null
        };
      });
    };
    $[26] = poll;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const showVoteView = t8;
  if (!poll) {
    return null;
  }
  const hasVoted = poll.voted.length > 0;
  let scopedState_4;
  let t9;
  if ($[28] !== interactionState || $[29] !== poll) {
    scopedState_4 = getScopedPollInteractionState(interactionState, poll);
    t9 = new Set(scopedState_4.selectedOptionNos);
    $[28] = interactionState;
    $[29] = poll;
    $[30] = scopedState_4;
    $[31] = t9;
  } else {
    scopedState_4 = $[30];
    t9 = $[31];
  }
  const selectedOptions = t9;
  let t10;
  if ($[32] !== poll.options) {
    t10 = poll.options.reduce(_temp4, 0);
    $[32] = poll.options;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const totalVotes = t10;
  let t11;
  if ($[34] !== poll.voted) {
    t11 = new Set(poll.voted);
    $[34] = poll.voted;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const votedOptionNos = t11;
  const showSelectionIndicator = votedOptionNos.size > 0;
  const canShowResults = !poll.is_open || hasVoted;
  const effectiveView = canShowResults && (!poll.is_open || scopedState_4.view === "results") ? "results" : "vote";
  const expandedOptionNo = scopedState_4.expandedOptionNo;
  const voteOptionGroupName = `drop-poll-${poll.id}`;
  let t12;
  if ($[36] !== hasVoted || $[37] !== voteMutation.isPending) {
    t12 = getPollSubmitLabel({
      isPending: voteMutation.isPending,
      hasVoted
    });
    $[36] = hasVoted;
    $[37] = voteMutation.isPending;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const submitButtonLabel = t12;
  const showResultsFooterActions = effectiveView === "results" && hasVoted;
  const showFooterAction = poll.is_open && showResultsFooterActions;
  const showMultichoiceSubmit = poll.multichoice && selectedOptions.size > 0;
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-text-[15px] tw-font-bold tw-text-white">Poll</div>;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const t14 = `tw-size-1.5 tw-flex-shrink-0 tw-rounded-full ${poll.is_open ? "tw-bg-green" : "tw-bg-iron-600"}`;
  let t15;
  if ($[40] !== t14) {
    t15 = <span className={t14} aria-hidden="true" />;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== poll) {
    t16 = getPollClosingLabel(poll);
    $[42] = poll;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t16) {
    t17 = <span className="tw-truncate">{t16}</span>;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t15 || $[47] !== t17) {
    t18 = <div className="tw-mb-3.5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">{t13}<div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-xs tw-font-medium tw-text-iron-400">{t15}{t17}</div></div>;
    $[46] = t15;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== drop.id || $[50] !== effectiveView || $[51] !== expandedOptionNo || $[52] !== handleSubmitVote || $[53] !== handleVoteOptionChange || $[54] !== hasVoted || $[55] !== poll || $[56] !== selectedOptions || $[57] !== showMultichoiceSubmit || $[58] !== showSelectionIndicator || $[59] !== submitButtonLabel || $[60] !== totalVotes || $[61] !== voteMutation.isPending || $[62] !== voteOptionGroupName || $[63] !== votedOptionNos) {
    t19 = effectiveView === "vote" && poll.is_open ? <div className="tw-flex tw-flex-col tw-gap-2">{poll.options.map(option_0 => <PollVoteOption key={option_0.option_no} option={option_0} checked={selectedOptions.has(option_0.option_no)} multichoice={poll.multichoice} groupName={voteOptionGroupName} disabled={voteMutation.isPending} onChange={handleVoteOptionChange} />)}{poll.multichoice && <div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-out ${showMultichoiceSubmit ? "tw-mt-3.5 tw-grid-rows-[1fr] tw-opacity-100" : "tw-mt-0 tw-grid-rows-[0fr] tw-opacity-0"}`}><div aria-hidden={!showMultichoiceSubmit} inert={!showMultichoiceSubmit} className="tw-overflow-hidden"><button type="button" disabled={!showMultichoiceSubmit || voteMutation.isPending} onClick={event => {
            event.stopPropagation();
            handleSubmitVote().catch(_temp5);
          }} className="tw-flex tw-w-full tw-transform-gpu tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white tw-bg-white tw-px-4 tw-py-2 tw-text-[13.5px] tw-font-bold tw-text-black tw-transition-all tw-duration-300 disabled:tw-cursor-not-allowed disabled:tw-border-white/[0.06] disabled:tw-bg-white/[0.025] disabled:tw-text-iron-500 desktop-hover:hover:tw-bg-iron-100">{submitButtonLabel}</button></div></div>}</div> : <div className="tw-flex tw-flex-col tw-gap-2">{poll.options.map(option_1 => {
        const isExpanded = expandedOptionNo === option_1.option_no;
        return <div key={option_1.option_no}><PollResultOption dropId={drop.id} option={option_1} totalVotes={totalVotes} isSelected={votedOptionNos.has(option_1.option_no)} isDimmed={hasVoted && !votedOptionNos.has(option_1.option_no)} isExpanded={isExpanded} multichoice={poll.multichoice} showSelectionIndicator={showSelectionIndicator} onToggle={optionNo_0 => setInteractionState(current_3 => {
            const currentState = getScopedPollInteractionState(current_3, poll);
            return {
              ...currentState,
              expandedOptionNo: currentState.expandedOptionNo === optionNo_0 ? null : optionNo_0
            };
          })} /><div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-out ${isExpanded ? "tw-mb-1 tw-mt-1 tw-grid-rows-[1fr] tw-opacity-100" : "tw-mb-0 tw-mt-0 tw-grid-rows-[0fr] tw-opacity-0"}`}><div aria-hidden={!isExpanded} inert={!isExpanded} className="tw-overflow-hidden"><div className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-3 tw-py-2.5"><PollOptionVoters dropId={drop.id} option={option_1} enabled={isExpanded} /></div></div></div></div>;
      })}</div>;
    $[49] = drop.id;
    $[50] = effectiveView;
    $[51] = expandedOptionNo;
    $[52] = handleSubmitVote;
    $[53] = handleVoteOptionChange;
    $[54] = hasVoted;
    $[55] = poll;
    $[56] = selectedOptions;
    $[57] = showMultichoiceSubmit;
    $[58] = showSelectionIndicator;
    $[59] = submitButtonLabel;
    $[60] = totalVotes;
    $[61] = voteMutation.isPending;
    $[62] = voteOptionGroupName;
    $[63] = votedOptionNos;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== showFooterAction || $[66] !== showResultsFooterActions || $[67] !== showVoteView || $[68] !== voteMutation.isPending) {
    t20 = showFooterAction && <div className="tw-mt-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/[0.06] tw-pt-3.5"><div className="tw-flex tw-items-center tw-justify-between tw-gap-2"><button type="button" disabled={voteMutation.isPending} onClick={event_0 => {
          event_0.stopPropagation();
          showVoteView();
        }} className="tw-flex tw-flex-shrink-0 tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-transparent tw-px-3 tw-py-1.5 tw-text-[13px] tw-font-medium tw-text-iron-300 tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-text-white">Change vote</button><span className={`tw-flex tw-h-8 tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-white/[0.06] tw-px-3.5 tw-text-[13px] tw-font-bold tw-text-white tw-transition-opacity tw-duration-500 ${showResultsFooterActions ? "tw-opacity-100" : "tw-pointer-events-none tw-opacity-0"}`} aria-hidden={!showResultsFooterActions}><span>Voted</span><CheckIcon className="tw-size-3.5 tw-text-green" strokeWidth={3} aria-hidden="true" /></span></div></div>;
    $[65] = showFooterAction;
    $[66] = showResultsFooterActions;
    $[67] = showVoteView;
    $[68] = voteMutation.isPending;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  let t21;
  if ($[70] !== t18 || $[71] !== t19 || $[72] !== t20) {
    t21 = <div role="presentation" className="tw-mb-2 tw-mt-3 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900/80 tw-p-4 tw-shadow-lg tw-backdrop-blur md:tw-max-w-2xl" onClick={stopPollEventPropagation} onKeyDown={stopPollEventPropagation}>{t18}{t19}{t20}</div>;
    $[70] = t18;
    $[71] = t19;
    $[72] = t20;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  return t21;
}
function _temp5() {}
function _temp4(total, option) {
  return total + option.votes;
}
function _temp3(a, b) {
  return a - b;
}
function _temp2() {}
function _temp() {}